import { useEffect, useState } from "react";
import { Trophy, Flame, Star, Sparkles, Award, Target, Crown, Zap } from "lucide-react";

const KEY = "silentscan_gamification_v1";

const ACHIEVEMENTS = [
  { id: "first_visit", title: "First Steps", desc: "Landed on SilentScan AI for the first time.", icon: Star, xp: 10 },
  { id: "health_check", title: "Body Aware", desc: "Completed a Health Assessment.", icon: Trophy, xp: 50 },
  { id: "mental_check", title: "Mind Aware", desc: "Completed a Mental Wellness check-in.", icon: Award, xp: 50 },
  { id: "streak_3", title: "3-Day Streak", desc: "Checked in 3 days in a row.", icon: Flame, xp: 30 },
  { id: "streak_7", title: "Weekly Warrior", desc: "Checked in 7 days in a row.", icon: Crown, xp: 100 },
  { id: "download_report", title: "Report Reader", desc: "Downloaded your first PDF report.", icon: Target, xp: 25 },
];

const loadState = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
};

const saveState = (s) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {}
};

const today = () => new Date().toISOString().slice(0, 10);
const yday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export default function GamificationSection() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let s = loadState();
    if (!s) {
      s = { xp: 10, streak: 1, lastVisit: today(), achievements: ["first_visit"] };
    } else if (s.lastVisit !== today()) {
      if (s.lastVisit === yday()) {
        s.streak = (s.streak || 0) + 1;
      } else {
        s.streak = 1;
      }
      s.lastVisit = today();
      s.xp = (s.xp || 0) + 5;
      s.achievements = s.achievements || [];
      if (s.streak >= 3 && !s.achievements.includes("streak_3")) {
        s.achievements.push("streak_3");
        s.xp += 30;
      }
      if (s.streak >= 7 && !s.achievements.includes("streak_7")) {
        s.achievements.push("streak_7");
        s.xp += 100;
      }
    }
    saveState(s);
    setState(s);
    const refresh = () => setState(loadState());
    window.addEventListener("silentscan:gamification-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("silentscan:gamification-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  if (!state) return null;
  const level = Math.max(1, Math.floor((state.xp || 0) / 100) + 1);
  const xpInLevel = (state.xp || 0) % 100;
  const unlocked = new Set(state.achievements || []);

  return (
    <section
      id="rewards"
      data-testid="gamification-section"
      className="relative py-28 md:py-36"
    >
      <div className="absolute inset-0 grid-radial opacity-50 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 rounded-full glass-card px-4 py-1.5 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-400">
              Rewards & Streaks
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-tight">
            Small habits,{" "}
            <span className="text-gradient-cyan">big streaks.</span>
          </h2>
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
            Every visit earns XP. Complete assessments to unlock achievements.
            Habits stick when they feel like a game.
          </p>
        </div>

        {/* XP / Streak / Level dashboard */}
        <div className="grid sm:grid-cols-3 gap-4">
          <StatCard
            testid="xp-card"
            icon={Zap}
            label="Total XP"
            value={state.xp || 0}
            hint={`Level ${level}`}
          />
          <StatCard
            testid="streak-card"
            icon={Flame}
            label="Daily Streak"
            value={`${state.streak || 0} days`}
            hint="Come back tomorrow to keep it alive"
          />
          <StatCard
            testid="level-card"
            icon={Crown}
            label="Level"
            value={level}
            hint={`${xpInLevel}/100 XP to next level`}
            progress={xpInLevel}
          />
        </div>

        {/* Achievements */}
        <div className="mt-10">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-4">
            Achievements
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ACHIEVEMENTS.map((a) => {
              const on = unlocked.has(a.id);
              const Icon = a.icon;
              return (
                <div
                  key={a.id}
                  data-testid={`achievement-${a.id}`}
                  className={`glass-card rounded-2xl p-5 flex items-start gap-3 transition-all ${
                    on
                      ? "border border-cyan-400/40 shadow-[0_0_20px_rgba(0,229,255,0.15)]"
                      : "border border-white/5 opacity-70"
                  }`}
                >
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${
                      on
                        ? "bg-gradient-to-br from-[#0B3D91] to-cyan-500 border-cyan-400/40 shadow-[0_0_15px_rgba(0,229,255,0.35)]"
                        : "bg-white/[0.03] border-white/10"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${on ? "text-white" : "text-zinc-500"}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div
                        className={`text-sm font-semibold ${
                          on ? "text-white" : "text-zinc-400"
                        }`}
                      >
                        {a.title}
                      </div>
                      <div
                        className={`text-[10px] uppercase tracking-widest font-semibold ${
                          on ? "text-cyan-400" : "text-zinc-500"
                        }`}
                      >
                        +{a.xp} XP
                      </div>
                      {on && (
                        <div className="text-[10px] text-emerald-400 uppercase tracking-widest">
                          Unlocked
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                      {a.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-8 text-center text-[11px] text-zinc-500">
          XP, streaks and achievements are stored locally in your browser and
          are meant for motivation — not medical progress.
        </p>
      </div>
    </section>
  );
}

const StatCard = ({ testid, icon: Icon, label, value, hint, progress }) => (
  <div
    data-testid={testid}
    className="glass-card rounded-3xl p-6 border border-white/10 relative overflow-hidden"
  >
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(0,229,255,0.35)]">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          {label}
        </div>
        <div className="font-display text-2xl font-semibold">{value}</div>
      </div>
    </div>
    {hint && (
      <div className="text-xs text-zinc-500 mt-3">{hint}</div>
    )}
    {progress != null && (
      <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#0B3D91] to-cyan-400 transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    )}
  </div>
);

// Utility to unlock achievements from anywhere in the app
export const unlockAchievement = (id, xpDelta = 25) => {
  try {
    const s = loadState() || {
      xp: 0,
      streak: 1,
      lastVisit: today(),
      achievements: [],
    };
    if (!s.achievements.includes(id)) {
      s.achievements.push(id);
      s.xp = (s.xp || 0) + xpDelta;
      saveState(s);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("silentscan:gamification-changed"));
      }
    }
  } catch {}
};
